import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  BookingColors,
  setBookingColor,
  setFontScale,
  setThemeMode,
  ThemeMode,
} from "../../store/slices/settingsSlice";
import { AppColors } from "../../constants/AppColors";

const availableColors = [
  "#16a34a",
  "#2563eb",
  "#7c3aed",
  "#f59e0b",
  "#dc2626",
  "#0d9488",
  "#db2777",
  "#64748b",
];

const fontOptions = [
  { labelKey: "settings.fontSmall", value: -2 },
  { labelKey: "settings.fontMedium", value: 0 },
  { labelKey: "settings.fontLarge", value: 2 },
  { labelKey: "settings.fontVeryLarge", value: 4 },
];

const themeOptions: {
  labelKey: string;
  value: ThemeMode;
  icon: React.ReactNode;
}[] = [
  {
    labelKey: "settings.themeSystem",
    value: "system",
    icon: <PhoneIphoneOutlinedIcon fontSize="small" />,
  },
  {
    labelKey: "settings.themeLight",
    value: "light",
    icon: <LightModeOutlinedIcon fontSize="small" />,
  },
  {
    labelKey: "settings.themeDark",
    value: "dark",
    icon: <DarkModeOutlinedIcon fontSize="small" />,
  },
];

interface ThemeSettingsCardProps {
  t: (key: string, values?: Record<string, string | number>) => string;
}

const ThemeSettingsCard = ({ t }: ThemeSettingsCardProps) => {
  const dispatch = useAppDispatch();
  const { fontScale, bookingColors, themeMode } = useAppSelector(
    (state) => state.settings,
  );

  const renderColorPicker = (status: keyof BookingColors, label: string) => {
    return (
      <Box key={status}>
        <Typography sx={{ fontWeight: 850, mb: 1 }}>{label}</Typography>

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {availableColors.map((color) => {
            const isSelected = bookingColors[status] === color;

            return (
              <Button
                key={color}
                onClick={() => dispatch(setBookingColor({ status, color }))}
                sx={{
                  minWidth: 34,
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  bgcolor: color,
                  border: isSelected
                    ? `3px solid ${AppColors.textPrimary}`
                    : `1px solid ${AppColors.border}`,
                  "&:hover": {
                    bgcolor: color,
                  },
                }}
              >
                {isSelected ? (
                  <CheckIcon
                    fontSize="small"
                    sx={{
                      color: AppColors.textOnPrimary,
                    }}
                  />
                ) : null}
              </Button>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Card sx={{ mb: 2, borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 950, mb: 2 }}>
          {t("settings.appAppearance")}
        </Typography>

        <Typography sx={{ fontWeight: 950, mb: 1 }}>
          {t("settings.theme")}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {themeOptions.map((option) => {
            const isSelected = themeMode === option.value;

            return (
              <Button
                key={option.value}
                variant={isSelected ? "contained" : "outlined"}
                startIcon={option.icon}
                onClick={() => dispatch(setThemeMode(option.value))}
              >
                {t(option.labelKey)}
              </Button>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 950, mb: 1 }}>
          {t("settings.fontSize")}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {fontOptions.map((option) => (
            <Button
              key={option.value}
              variant={fontScale === option.value ? "contained" : "outlined"}
              onClick={() => dispatch(setFontScale(option.value))}
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 950, mb: 2 }}>
          {t("settings.bookingColors")}
        </Typography>

        <Stack spacing={2}>
          {renderColorPicker("paid", t("booking.paid"))}
          {renderColorPicker("deposit", t("booking.deposit"))}
          {renderColorPicker("unpaid", t("booking.unpaid"))}
          {renderColorPicker("cancelled", t("booking.cancelled"))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ThemeSettingsCard;
